import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import {
  buildClosedMeetingMessage,
  getMeetingProviderLabel,
  isAlreadyClosedError,
  isDirectCallMeeting,
  isMeetingActive,
  isMeetingOrganizer,
} from '../../utils/meetingUtils';
import CallNotesInput from './CallNotesInput';
import CloseMeetingDialog from './CloseMeetingDialog';
import { recordCallJoin } from '../../services/attendanceService';

const MeetingTimelineCard = ({
  message,
  currentUserId,
  onNotePosted,
  onMeetingUpdated,
  notesExpanded = false,
  channelNoteMessages = [],
  reserveTopActionsSpace = false,
  onJoinDirectCall,
}) => {
  const [showNotesInput, setShowNotesInput] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closing, setClosing] = useState(false);

  const meeting = message?.meetingInfo;
  if (!meeting?.joinUrl && !isDirectCallMeeting(meeting)) return null;

  const active = isMeetingActive(meeting);
  const organizer = isMeetingOrganizer(meeting, currentUserId);
  const isDirect = isDirectCallMeeting(meeting);
  const providerLabel = getMeetingProviderLabel(meeting.provider);
  const title = meeting.label || providerLabel;
  const notesOpen = showNotesInput || notesExpanded;

  const handleJoin = () => {
    if (isDirect && typeof onJoinDirectCall === 'function') {
      onJoinDirectCall(message);
      return;
    }
    if (meeting.joinUrl && !meeting.joinUrl.startsWith('workloop://')) {
      window.open(meeting.joinUrl, '_blank', 'noopener,noreferrer');
      recordCallJoin({
        title: title || providerLabel,
        channelId: message.channelId,
        messageId: message.id,
        provider: meeting.provider || providerLabel,
        liveSession: false,
      }).catch(() => {});
    }
  };

  const handleCloseCall = async () => {
    if (closing) return;
    setClosing(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(
        `${API_URL}/messages/${message.id}/close-meeting`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onMeetingUpdated?.(data);
      setShowCloseDialog(false);
    } catch (err) {
      if (isAlreadyClosedError(err)) {
        onMeetingUpdated?.(buildClosedMeetingMessage(message, currentUserId));
        setShowCloseDialog(false);
        return;
      }
      console.error('Failed to close call:', err);
      alert(typeof err.response?.data === 'string'
        ? err.response.data.replace(/^Error:\s*/i, '')
        : 'Failed to close call. Please try again.');
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      <div
        className={`w-full min-w-0 rounded-xl border ${
          active
            ? 'border-indigo-200/70 dark:border-indigo-800/50 bg-white/50 dark:bg-gray-900/30'
            : 'border-gray-200 dark:border-gray-300 bg-white shadow-sm'
        } ${notesOpen ? 'overflow-visible' : 'overflow-hidden'}`}
      >
        <div className={`px-3 py-2.5 flex items-start gap-3 ${reserveTopActionsSpace ? 'pr-24' : ''}`}>
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
            <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold truncate ${active ? 'text-gray-900 dark:text-white' : 'text-gray-900'}`}>{title}</p>
            <p className={`text-xs mt-0.5 ${active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500'}`}>
              {active
                ? `${isDirect ? 'Direct call' : providerLabel} · Live`
                : `${isDirect ? 'Direct call' : providerLabel} · Ended`}
            </p>
          </div>
        </div>

        {active && (
          <>
            <div className="px-3 pb-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleJoin}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                Join call
              </button>
              <button
                type="button"
                onClick={() => setShowNotesInput((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {notesOpen ? 'Hide notes' : 'Add call notes'}
              </button>
              {organizer && (
                <button
                  type="button"
                  onClick={() => setShowCloseDialog(true)}
                  disabled={closing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                  Close call
                </button>
              )}
            </div>

            {notesOpen && (
              <div className="px-3 pb-3 border-t border-indigo-200/50 dark:border-indigo-800/40 pt-3">
                <CallNotesInput
                  meetingMessageId={message.id}
                  autoFocus={notesExpanded}
                  compact
                  onNotePosted={onNotePosted}
                />
              </div>
            )}
          </>
        )}

        {!active && (
          <div className="px-3 pb-3">
            <p className="text-xs text-gray-500">
              Call ended{meeting.closedAt ? ` · ${new Date(meeting.closedAt).toLocaleString()}` : ''}
            </p>
          </div>
        )}
      </div>

      <CloseMeetingDialog
        isOpen={showCloseDialog}
        onClose={() => setShowCloseDialog(false)}
        onSubmit={handleCloseCall}
        meetingLabel={title}
        noteMessages={channelNoteMessages}
        loading={closing}
      />
    </>
  );
};

export default MeetingTimelineCard;
